import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useConsoleStore } from '../store';
import { apiFetch, apiUrl } from '../lib/api';

export function Verify() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'verifying' | 'error'>('verifying');
  const [errorMsg, setErrorMsg] = useState('');
  const fired = useRef(false);
  const { setActiveStoreId } = useConsoleStore();

  useEffect(() => {
    if (fired.current) return;
    fired.current = true;
    const token = searchParams.get('token');
    const domain = searchParams.get('domain');
    const wpState = searchParams.get('wp_state');
    const wpCallback = searchParams.get('wp_callback');
    if (!token) { setStatus('error'); setErrorMsg('Missing token'); return; }

    const url = domain
      ? apiUrl(
        `/api/auth/verify?token=${encodeURIComponent(token)}&domain=${encodeURIComponent(domain)}${wpState && wpCallback ? '&wordpress_connect=1' : ''}`,
      )
      : apiUrl(`/api/auth/verify?token=${encodeURIComponent(token)}`);

    fetch(url, { credentials: 'include' })
      .then(async res => {
        if (!res.ok) {
          const b = await res.json();
          setStatus('error'); setErrorMsg(b.error || 'Verification failed');
          return;
        }
        const body = await res.json();
        if (domain && wpState && wpCallback) {
          try {
            const connect = await apiFetch<{ code: string }>('/api/console/wordpress/connect-code', {
              method: 'POST',
              body: JSON.stringify({ domain }),
            });
            const callbackUrl = new URL(wpCallback);
            callbackUrl.searchParams.set('reach_code', connect.code);
            callbackUrl.searchParams.set('reach_state', wpState);
            window.location.assign(callbackUrl.toString());
            return;
          } catch {
            try {
              const callbackUrl = new URL(wpCallback);
              callbackUrl.searchParams.set('reach_error', 'connect_failed');
              callbackUrl.searchParams.set('reach_state', wpState);
              window.location.assign(callbackUrl.toString());
              return;
            } catch {
              setStatus('error');
              setErrorMsg('Could not finish the WordPress connection. Please return to WordPress and try again.');
              return;
            }
          }
        }
        if (body.storeId) {
          setActiveStoreId(body.storeId);
          navigate('/appearance', { replace: true });
        } else {
          navigate('/', { replace: true });
        }
      })
      .catch(() => { setStatus('error'); setErrorMsg('Network error'); });
  }, []);

  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50 px-4">
        <div className="text-center space-y-3">
          <p className="text-zinc-900 font-semibold">Link expired or invalid</p>
          <p className="text-sm text-zinc-500">{errorMsg}</p>
          <Link to="/login" className="text-sm text-zinc-900 underline underline-offset-2">Request a new link</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50">
      <div className="h-6 w-6 animate-spin rounded-full border-2 border-zinc-300 border-t-zinc-900" />
    </div>
  );
}
